import { logger } from '../lib/utils';
import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage fullscreen mode on mobile devices.
 * This helps hide Android/iOS system navigation bars during gameplay.
 */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if fullscreen is supported
    const doc = document.documentElement;
    const supported = !!(
      doc.requestFullscreen ||
      doc.webkitRequestFullscreen ||
      doc.mozRequestFullScreen ||
      doc.msRequestFullscreen
    );
    setIsSupported(supported);

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const requestFullscreen = useCallback(async () => {
    const doc = document.documentElement;
    try {
      if (doc.requestFullscreen) {
        await doc.requestFullscreen({ navigationUI: 'hide' });
      } else if (doc.webkitRequestFullscreen) {
        await doc.webkitRequestFullscreen();
      } else if (doc.mozRequestFullScreen) {
        await doc.mozRequestFullScreen();
      } else if (doc.msRequestFullscreen) {
        await doc.msRequestFullscreen();
      }
      return true;
    } catch (error) {
      logger.log('Fullscreen request failed:', error);
      return false;
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      return true;
    } catch (error) {
      logger.log('Exit fullscreen failed:', error);
      return false;
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      return await exitFullscreen();
    } else {
      return await requestFullscreen();
    }
  }, [isFullscreen, requestFullscreen, exitFullscreen]);

  return {
    isFullscreen,
    isSupported,
    requestFullscreen,
    exitFullscreen,
    toggleFullscreen
  };
}
